import merge from 'lodash/merge'
import isNil from 'lodash/isNil'
import type { ConfirmDialogConfig } from './ConfirmDialogContext'
import { useConfirmDialogContext } from './ConfirmDialogContext'

export type OnConfirmFunction<T = any> = (args?: any) => Promise<T>
export type OnCancelFunction<T = any> = () => Promise<T>

export type ConfirmDialogOptions<T = any> = ConfirmDialogConfig & {
  confirmed?: OnConfirmFunction<T>
  cancelled?: OnCancelFunction<T>
}

export const useConfirmDialog = <T = void>({ confirmed, cancelled, ...config }: ConfirmDialogOptions<T> = {}) => {
  const ctx = useConfirmDialogContext()

  config = merge(
    {},
    {
      title: '',
      text: 'Are you sure?',
      icon: 'warning',
      buttons: {
        cancel: 'Cancel',
        confirm: {
          text: 'OK',
        },
      },
      dangerMode: true,
    },
    config
  )

  const confirm = async <T>(
    confirmedFunc?: OnConfirmFunction<T>,
    cancelled?: OnCancelFunction<T>,
    confirmConfig?: ConfirmDialogOptions<T>
  ): Promise<T> => {
    const alertConfig = merge({}, config, confirmConfig)

    const confirmResult = await ctx.showDialog(alertConfig)
    if (!isNil(confirmResult) && confirmedFunc) {
      return confirmedFunc(confirmResult)
    } else {
      if (cancelled) {
        return cancelled()
      }
    }
  }

  const _onConfirm = async (args?: any) => {
    return confirm(() => confirmed(args), cancelled, config)
  }

  return { confirm, config, onConfirm: _onConfirm }
}
